import React, { useEffect, useState } from "react";
import Card from "components/card";
import { getEmployees } from "services/hrms.api";
import { useNavigate } from "react-router-dom";

export default function WeeklyRevenue({
  onViewAll = () => {},
  // onSend = (empId) => alert(`Send wishes to ${empId}`),
}) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setError("");
        setLoading(true);
        const { data } = await getEmployees(); // ⬅ uses your API
        const grouped = buildBirthdayGroups(data || []);
        setGroups(grouped);
      } catch (e) {
        console.error(e);
        setError("Failed to load birthdays.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 🔹 Only show up to 4 employees total in this card
  const maxVisible = 3;
  const visibleGroups = limitGroups(groups, maxVisible);

  const handleViewAll = () => {
    onViewAll(); // keep hook if you want to track
    navigate("/admin/hrms/employees");
  };

  return (
    <Card extra="flex flex-col bg-white w-full rounded-3xl p-0">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-white/10">
        <h2 className="text-base font-semibold text-navy-700 dark:text-white">
          Birthdays
        </h2>
        <button
          onClick={handleViewAll}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:text-white/80 dark:hover:bg-white/10"
        >
          View All
        </button>
      </div>

      {/* Content */}
      <div className="space-y-5 p-5">
        {loading && (
          <div className="text-xs text-gray-500 dark:text-white/70">
            Loading birthdays...
          </div>
        )}

        {!loading && error && (
          <div className="border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200 rounded-lg border p-3 text-xs">
            {error}
          </div>
        )}

        {!loading && !error && visibleGroups.length === 0 && (
          <div className="text-xs text-gray-500 dark:text-white/60">
            No upcoming birthdays.
          </div>
        )}

        {!loading &&
          !error &&
          visibleGroups.map((grp) =>
            grp.items?.length ? (
              <div key={grp.label} className="space-y-3">
                <div className="text-sm font-semibold text-gray-700 dark:text-white/90">
                  {grp.label}
                </div>

                {grp.items.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-xl border border-dashed border-gray-200 bg-white px-3 py-3 shadow-sm dark:border-white/10 dark:bg-white/5"
                  >
                    <div className="flex items-center gap-3">
                      {p.avatar ? (
                        <img
                          src={p.avatar}
                          alt={p.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 text-xs font-semibold text-teal-700">
                          {getInitials(p.name)}
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-semibold text-navy-700 dark:text-white">
                          {p.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {p.role || "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : null
          )}
      </div>
    </Card>
  );
}

/* ---------------- helpers ---------------- */

// 🔹 Limit total employees shown across all groups to maxVisible
function limitGroups(groups, maxVisible) {
  const flat = [];
  for (const grp of groups) {
    for (const item of grp.items) {
      if (flat.length >= maxVisible) break;
      flat.push({ ...item, _groupLabel: grp.label });
    }
    if (flat.length >= maxVisible) break;
  }

  const grouped = {};
  for (const item of flat) {
    if (!grouped[item._groupLabel]) {
      grouped[item._groupLabel] = { label: item._groupLabel, items: [] };
    }
    const { _groupLabel, ...rest } = item;
    grouped[item._groupLabel].items.push(rest);
  }

  return Object.values(grouped);
}

function buildBirthdayGroups(employees) {
  const today = new Date();
  const todayKey = monthDayKey(today);

  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const tomorrowKey = monthDayKey(tomorrow);

  const map = {
    Today: [],
    Tomorrow: [],
  };
  const othersMap = {};

  for (const emp of employees) {
    if (!emp.dob) continue;

    const dob = new Date(emp.dob);
    if (isNaN(dob.getTime())) continue;

    const mdKey = monthDayKey(dob);

    const name = joinName(emp.firstName, emp.lastName, emp.code);
    const role = emp.designation || emp.role || "";
    const avatar = emp.avatar || null;

    const normalized = {
      id: emp.id || emp._id,
      name,
      role,
      avatar,
    };

    if (mdKey === todayKey) {
      map.Today.push(normalized);
      continue;
    }

    if (mdKey === tomorrowKey) {
      map.Tomorrow.push(normalized);
      continue;
    }

    const nextDate = nextBirthdayDate(dob, today);
    const label = formatDateLabel(nextDate);
    const labelKey = `${nextDate.getFullYear()}-${nextDate.getMonth()}-${nextDate.getDate()}`;

    if (!othersMap[labelKey]) {
      othersMap[labelKey] = {
        label,
        sortDate: nextDate,
        items: [],
      };
    }
    othersMap[labelKey].items.push(normalized);
  }

  const groups = [];

  if (map.Today.length) {
    groups.push({ label: "Today", items: map.Today });
  }
  if (map.Tomorrow.length) {
    groups.push({ label: "Tomorrow", items: map.Tomorrow });
  }

  const others = Object.values(othersMap).sort(
    (a, b) => a.sortDate - b.sortDate
  );

  return [
    ...groups,
    ...others.map((g) => ({ label: g.label, items: g.items })),
  ];
}

function monthDayKey(date) {
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return `${m}-${d}`;
}

function nextBirthdayDate(dob, fromDate) {
  const thisYear = fromDate.getFullYear();
  let candidate = new Date(thisYear, dob.getMonth(), dob.getDate());

  if (candidate < startOfDay(fromDate)) {
    candidate = new Date(thisYear + 1, dob.getMonth(), dob.getDate());
  }

  return candidate;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatDateLabel(date) {
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function joinName(first, last, fallback) {
  const f = (first || "").trim();
  const l = (last || "").trim();
  const full = `${f} ${l}`.trim();
  return full || fallback || "Unknown";
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
